/**
 * Typed client + shared types for the Store admin area.
 * Wraps the `/api/store/*` operator routes on the CRM backend.
 */
import { api } from "@/lib/api";

export interface StoreVariant {
  id: string;
  product_id: string;
  title: string;
  option_values: Record<string, string>;
  sku: string | null;
  amount_cents: number | null;
  compare_at_cents: number | null;
  inventory_quantity: number;
  image_url: string | null;
  position: number;
}

export interface StoreProduct {
  id: string;
  name: string;
  slug: string | null;
  subtitle: string | null;
  description: string | null;
  long_description: string | null;
  image_url: string | null;
  gallery: string[];
  highlights: string[];
  pricing_type: "one_time" | "subscription";
  amount_cents: number;
  min_amount_cents: number;
  max_amount_cents: number;
  compare_at_cents: number | null;
  currency: string;
  price_display: string;
  recurring_interval: string | null;
  recurring_interval_count: number | null;
  product_type: "physical" | "digital" | "service" | "membership";
  fulfillment: "instant" | "shipped" | "contact";
  payment_link: string | null;
  badge: string | null;
  published: boolean;
  featured: boolean;
  archived: boolean;
  sort_order: number;
  sku: string | null;
  track_inventory: boolean;
  inventory_quantity: number;
  allow_backorder: boolean;
  weight_grams: number | null;
  has_variants: boolean;
  options: { name: string; values: string[] }[];
  requires_shipping: boolean;
  resource_count?: number;
  collection_ids?: string[];
  variants?: StoreVariant[];
}

export interface StoreResource {
  id: string;
  product_id: string;
  title: string;
  description: string | null;
  resource_type: "link" | "file" | "video" | "text" | "download" | "embed";
  url: string | null;
  body: string | null;
  sort_order: number;
}

export interface StoreCollection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  published: boolean;
  sort_order: number;
  product_count: number;
  product_ids: string[];
}

export interface StoreOrderItem {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  product_name: string;
  variant_title: string | null;
  sku: string | null;
  image_url: string | null;
  quantity: number;
  unit_amount_cents: number;
  total_cents: number;
  requires_shipping: boolean;
}

export interface StoreAddress {
  name?: string;
  phone?: string;
  line1?: string;
  line2?: string | null;
  city?: string;
  region?: string | null;
  postal_code?: string | null;
  country?: string;
}

export interface StoreOrder {
  id: string;
  order_number: number;
  customer_user_id: string | null;
  contact_id: string | null;
  email: string | null;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  refunded_cents: number;
  status: string;
  fulfillment_status: string;
  tracking_number: string | null;
  tracking_url: string | null;
  shipping_carrier: string | null;
  fulfilled_at: string | null;
  provider: string | null;
  discount_code: string | null;
  shipping_rate_name: string | null;
  shipping_address: StoreAddress | null;
  billing_address: StoreAddress | null;
  customer_note: string | null;
  created_at: string;
  paid_at: string | null;
  canceled_at: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  item_count?: number;
  items?: StoreOrderItem[];
}

export interface StoreCustomer {
  user_id: string;
  contact_id: string | null;
  name: string | null;
  email: string | null;
  created_at: string;
  entitlements: number;
  orders: number;
  spent_cents: number;
}

export interface StoreEntitlement {
  id: string;
  product_id: string;
  product_name: string;
  status: string;
  source: string;
  granted_at: string;
}

export interface StoreDiscount {
  id: string;
  code: string;
  kind: "percent" | "fixed" | "free_shipping";
  value: number;
  applies_to: "all" | "products" | "collections";
  applies_ids: string[];
  min_subtotal_cents: number | null;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  max_uses_per_customer: number | null;
  used_count: number;
  active: boolean;
  created_at: string;
}

export interface StoreShippingRate {
  id: string;
  name: string;
  amount_cents: number;
  min_subtotal_cents: number | null;
  max_subtotal_cents: number | null;
  free_over_cents: number | null;
  position: number;
}

export interface StoreShippingZone {
  id: string;
  name: string;
  countries: string[];
  position: number;
  rates: StoreShippingRate[];
}

export interface StoreStats {
  products: number;
  published: number;
  customers: number;
  orders: number;
  paid_orders: number;
  revenue_cents: number;
  revenue_30d_cents: number;
  orders_30d: number;
  aov_cents: number;
  active_entitlements: number;
  pending_orders: number;
  unfulfilled_orders: number;
  enquiries: number;
  daily: { day: string; revenue_cents: number; orders: number }[];
  top_products: { name: string; quantity: number; revenue_cents: number }[];
}

export type StoreSettings = Record<string, unknown> & {
  store_name?: string;
  currency?: string;
  guest_checkout?: boolean;
  receipts_enabled?: boolean;
  manual_payments_enabled?: boolean;
  manual_payment_instructions?: string;
  order_prefix?: string;
  taxes?: {
    enabled?: boolean;
    label?: string;
    rate_bps?: number;
    include_shipping?: boolean;
    country_overrides?: { country: string; rate_bps: number }[];
  };
  announcement?: string;
  announcement_enabled?: boolean;
  hero_headline?: string;
  hero_sub?: string;
  cta_label?: string;
  contact_email?: string;
  contact_phone?: string;
  social?: Record<string, string>;
  policies?: Record<string, string>;
};

export const storeApi = {
  stats: () => api.get<StoreStats>("/store/stats"),

  products: () => api.get<{ products: StoreProduct[] }>("/store/products"),
  createProduct: (body: Partial<StoreProduct> & { name: string; amount_cents: number }) =>
    api.post<{ product: StoreProduct }>("/store/products", body),
  updateProduct: (id: string, body: Partial<StoreProduct>) =>
    api.patch<{ product: StoreProduct }>(`/store/products/${id}`, body),
  deleteProduct: (id: string) => api.delete<void>(`/store/products/${id}`),
  saveVariants: (productId: string, body: {
    options: { name: string; values: string[] }[];
    variants: Partial<StoreVariant>[];
  }) => api.put<{ variants: StoreVariant[] }>(`/store/products/${productId}/variants`, body),

  resources: (productId: string) =>
    api.get<{ resources: StoreResource[] }>(`/store/products/${productId}/resources`),
  createResource: (productId: string, body: Partial<StoreResource> & { title: string }) =>
    api.post<{ resource: StoreResource }>(`/store/products/${productId}/resources`, body),
  updateResource: (id: string, body: Partial<StoreResource>) =>
    api.patch<{ resource: StoreResource }>(`/store/resources/${id}`, body),
  deleteResource: (id: string) => api.delete<void>(`/store/resources/${id}`),

  collections: () => api.get<{ collections: StoreCollection[] }>("/store/collections"),
  createCollection: (body: Partial<StoreCollection> & { name: string }) =>
    api.post<{ id: string; slug: string }>("/store/collections", body),
  updateCollection: (id: string, body: Partial<StoreCollection>) =>
    api.patch<{ ok: true }>(`/store/collections/${id}`, body),
  deleteCollection: (id: string) => api.delete<void>(`/store/collections/${id}`),
  setCollectionProducts: (id: string, product_ids: string[]) =>
    api.put<{ ok: true }>(`/store/collections/${id}/products`, { product_ids }),

  orders: (params: { limit?: number; offset?: number; status?: string; fulfillment?: string; search?: string } = {}) =>
    api.get<{ orders: StoreOrder[]; total: number }>("/store/orders", params),
  order: (id: string) => api.get<{ order: StoreOrder }>(`/store/orders/${id}`),
  fulfillOrder: (id: string, body: { tracking_number?: string | null; tracking_url?: string | null; carrier?: string | null; notify?: boolean }) =>
    api.post<{ ok: true }>(`/store/orders/${id}/fulfill`, body),
  markOrderPaid: (id: string) => api.post<{ ok: true }>(`/store/orders/${id}/mark-paid`),
  cancelOrder: (id: string) => api.post<{ ok: true }>(`/store/orders/${id}/cancel`),
  refundOrder: (id: string, amount_cents?: number) =>
    api.post<{ ok: true }>(`/store/orders/${id}/refund`, amount_cents ? { amount_cents } : {}),
  resendReceipt: (id: string) => api.post<{ ok: true }>(`/store/orders/${id}/resend-receipt`),

  customers: () => api.get<{ customers: StoreCustomer[] }>("/store/customers"),
  customerEntitlements: (userId: string) =>
    api.get<{ entitlements: StoreEntitlement[] }>(`/store/customers/${userId}/entitlements`),
  grant: (customer_user_id: string, product_id: string) =>
    api.post<{ id: string }>("/store/entitlements", { customer_user_id, product_id }),
  revoke: (id: string) => api.delete<void>(`/store/entitlements/${id}`),

  discounts: () => api.get<{ discounts: StoreDiscount[] }>("/store/discounts"),
  createDiscount: (body: Partial<StoreDiscount> & { code: string; kind: StoreDiscount["kind"] }) =>
    api.post<{ discount: StoreDiscount }>("/store/discounts", body),
  updateDiscount: (id: string, body: Partial<StoreDiscount>) =>
    api.patch<{ discount: StoreDiscount }>(`/store/discounts/${id}`, body),
  deleteDiscount: (id: string) => api.delete<void>(`/store/discounts/${id}`),

  shipping: () => api.get<{ zones: StoreShippingZone[] }>("/store/shipping"),
  createZone: (body: { name: string; countries: string[]; position?: number }) =>
    api.post<{ id: string }>("/store/shipping/zones", body),
  updateZone: (id: string, body: Partial<{ name: string; countries: string[]; position: number }>) =>
    api.patch<{ ok: true }>(`/store/shipping/zones/${id}`, body),
  deleteZone: (id: string) => api.delete<void>(`/store/shipping/zones/${id}`),
  createRate: (zoneId: string, body: Partial<StoreShippingRate> & { name: string; amount_cents: number }) =>
    api.post<{ id: string }>(`/store/shipping/zones/${zoneId}/rates`, body),
  updateRate: (id: string, body: Partial<StoreShippingRate>) =>
    api.patch<{ ok: true }>(`/store/shipping/rates/${id}`, body),
  deleteRate: (id: string) => api.delete<void>(`/store/shipping/rates/${id}`),

  settings: () => api.get<{ settings: StoreSettings }>("/store/settings"),
  saveSettings: (settings: Partial<StoreSettings>) =>
    api.put<{ settings: StoreSettings }>("/store/settings", { settings }),
};

export const PRODUCT_TYPE_LABELS: Record<string, string> = {
  physical: "Physical product",
  digital: "Digital / download",
  service: "Service",
  membership: "Membership",
};

export const FULFILLMENT_LABELS: Record<string, string> = {
  shipped: "Ship it (physical goods)",
  instant: "Instant access (digital content)",
  contact: "Enquiry first (you follow up, no card charged)",
};

export const ORDER_STATUS_COLORS: Record<string, string> = {
  paid: "emerald",
  free: "emerald",
  pending: "amber",
  enquiry: "violet",
  refunded: "rose",
  partially_refunded: "amber",
  canceled: "zinc",
};

export const ORDER_STATUS_LABELS: Record<string, string> = {
  paid: "Paid",
  free: "Free",
  pending: "Pending payment",
  enquiry: "Enquiry",
  refunded: "Refunded",
  partially_refunded: "Partially refunded",
  canceled: "Canceled",
};

export const centsToStr = (c: number) => (c / 100).toFixed(2);
export const strToCents = (s: string) => Math.round(parseFloat(s || "0") * 100) || 0;
